import { create } from 'zustand';

import { THIRTY_SECONDS } from '@/constants/time';

/** How long each announcement holds the banner before the next one takes it. */
export const ANNOUNCEMENT_ROTATION_INTERVAL_MS = THIRTY_SECONDS;

type AnnouncementRotationState = {
  tick: number;
  isPaused: boolean;
  advance: () => void;
  setPaused: (isPaused: boolean) => void;
  reset: () => void;
};

/**
 * Where the rotation has got to, as a count rather than an index.
 *
 * Every caller derives the announcement on screen from `tick % eligibleCount`,
 * so they cannot disagree about which one is showing. An index would have to be
 * advanced by somebody, and on native there is nobody to be: Inbox, Library and
 * Directory are all mounted at once in the tab navigator, so three banners exist
 * whenever a reader is anywhere near one. A shared count has no owner to elect.
 *
 * It also survives the list changing underneath it. The query replaces the whole
 * list every minute; a modulo lands somewhere valid
 * whatever the new length is, where a stored index would point at whatever moved
 * into that slot.
 */
export const useAnnouncementRotationStore = create<AnnouncementRotationState>((set) => ({
  tick: 0,
  isPaused: false,
  advance: () => set((state) => ({ tick: state.tick + 1 })),
  setPaused: (isPaused) => set({ isPaused }),
  reset: () => set({ tick: 0 }),
}));

let subscriberCount = 0;

let intervalId: ReturnType<typeof setInterval> | null = null;

function startInterval() {
  intervalId = setInterval(() => {
    if (useAnnouncementRotationStore.getState().isPaused) {
      return;
    }

    useAnnouncementRotationStore.getState().advance();
  }, ANNOUNCEMENT_ROTATION_INTERVAL_MS);
}

function stopInterval() {
  if (intervalId !== null) {
    clearInterval(intervalId);

    intervalId = null;
  }
}

/**
 * Starts the rotation clock, or joins the one already running.
 *
 * Module-level rather than per-component for the reason the tick is shared: with
 * three banners mounted, a timer per caller is three timers advancing one
 * counter, and the rotation runs at three times the interval it claims. Ref
 * counting keeps exactly one alive for as long as anything is watching.
 *
 * Returns the unsubscribe, shaped for `useEffect`.
 */
export function subscribeToAnnouncementRotation(): () => void {
  subscriberCount += 1;

  if (intervalId === null) {
    startInterval();
  }

  return () => {
    subscriberCount -= 1;

    if (subscriberCount <= 0) {
      subscriberCount = 0;

      stopInterval();
    }
  };
}

/** Test seam: drops the shared interval and its subscribers between cases. */
export function teardownAnnouncementRotation() {
  subscriberCount = 0;

  stopInterval();

  useAnnouncementRotationStore.setState({ tick: 0, isPaused: false });
}
